import { useEffect, useState } from 'react';
import {
  Download,
  Check,
  ExternalLink,
  Monitor,
  Apple,
  Terminal,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useOSDetection } from '../../hooks/useOSDetection';
import { useArchDetection } from '../../hooks/useArchDetection';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../Toast';
import { DownloadCard } from './DownloadCard';
import type { OSType } from '../../hooks/useOSDetection';
import type { DownloadConfig } from './types';

// GCS-hosted download config URL (always up-to-date, even if frontend isn't redeployed)
const GCS_DOWNLOAD_CONFIG_URL =
  'https://storage.googleapis.com/aigod-videos/installers/download-config.json';

// Platform order for display
const PLATFORM_ORDER: OSType[] = ['windows', 'macos', 'linux'];

// Platform icons
const PlatformIcons: Record<OSType, React.ReactNode> = {
  windows: <Monitor className="h-8 w-8 text-blue-600" />,
  macos: <Apple className="h-8 w-8 text-secondary-700 dark:text-secondary-300" />,
  linux: <Terminal className="h-8 w-8 text-orange-600" />,
  unknown: <Download className="h-8 w-8 text-secondary-600" />,
};

// Format date for display
function formatReleaseDate(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } catch {
    return 'Unknown';
  }
}

// Loading skeleton component
function DownloadCardSkeleton() {
  return (
    <div className="flex flex-col p-6 rounded-xl border-2 border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 animate-pulse">
      <div className="flex justify-center mb-4 mt-2">
        <div className="w-16 h-16 rounded-xl bg-secondary-200 dark:bg-secondary-700" />
      </div>
      <div className="h-6 bg-secondary-200 dark:bg-secondary-700 rounded w-2/3 mx-auto mb-2" />
      <div className="h-4 bg-secondary-200 dark:bg-secondary-700 rounded w-1/2 mx-auto mb-6" />
      <div className="space-y-2 mt-auto">
        <div className="h-12 bg-secondary-200 dark:bg-secondary-700 rounded" />
      </div>
    </div>
  );
}

// Error component
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="empty-state-centered">
      <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
        <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
      </div>
      <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-2">
        {t('download.error.title')}
      </h3>
      <p className="text-secondary-600 dark:text-secondary-400 mb-4 text-center max-w-md">
        {message}
      </p>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
      >
        {t('download.error.retry')}
      </button>
    </div>
  );
}

export function DownloadPage() {
  const { t } = useTranslation();
  const { os } = useOSDetection();
  const { arch } = useArchDetection();
  const { toasts, showToast, removeToast } = useToast();

  // State for dynamic config loading
  const [config, setConfig] = useState<DownloadConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch download configuration from GCS (always current) with local fallback
  const fetchConfig = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let data: DownloadConfig | null = null;

      // Try GCS first (always up-to-date, independent of frontend deploy)
      try {
        const gcsResponse = await fetch(GCS_DOWNLOAD_CONFIG_URL);
        if (gcsResponse.ok) {
          data = await gcsResponse.json();
        }
      } catch {
        // GCS fetch failed (CORS, network, etc.) - fall through to local
      }

      // Fall back to local static config
      if (!data) {
        const localResponse = await fetch('/download-config.json');
        if (!localResponse.ok) {
          throw new Error(`HTTP error! status: ${localResponse.status}`);
        }
        data = await localResponse.json();
      }

      setConfig(data);
    } catch (err) {
      console.error('Failed to fetch download config:', err);
      setError(t('download.error.failedToLoad'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sort platforms to show detected OS first
  const sortedPlatforms = [...PLATFORM_ORDER].sort((a, b) => {
    if (a === os) return -1;
    if (b === os) return 1;
    return 0;
  });

  const handleDownload = (url: string, filename: string) => {
    // Installers are 50-200 MB — proxying through the backend would exhaust
    // server memory. GCS objects are uploaded with Content-Disposition: attachment
    // (set by upload_installers_to_gcs.py), so <a download> works correctly here.
    // NOTE: we cannot verify whether the GCS URL is reachable before triggering
    // the browser download (CORS prevents a HEAD check from the frontend), so the
    // toast is intentionally neutral — it describes what we requested, not the
    // outcome, which the browser will surface on its own if the fetch fails.
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast('info', t('download.downloadRequested'), t('download.downloadRequestedMessage'));
  };

  return (
    <div
      className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      data-testid="download-page"
    >
      {/* Hero Section */}
      <div className="text-center mb-10 sm:mb-14">
        <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl mb-4 sm:mb-6">
          <Download className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
        </div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-secondary-900 dark:text-secondary-100 mb-3">
          {t('download.title')}
        </h1>
        <p className="text-base sm:text-lg text-secondary-600 dark:text-secondary-400 max-w-xl mx-auto">
          {t('download.subtitle')}
        </p>
      </div>

      {/* Download Cards - Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {[1, 2, 3].map((i) => (
            <DownloadCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Download Cards - Error State */}
      {error && !isLoading && <ErrorState message={error} onRetry={fetchConfig} />}

      {/* Download Cards - Success State */}
      {config && !isLoading && !error && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {sortedPlatforms.map((platform) => {
            const platformConfig = config.platforms[platform as keyof typeof config.platforms];
            if (!platformConfig) return null;

            return (
              <DownloadCard
                key={platform}
                platform={platform}
                icon={PlatformIcons[platform]}
                isRecommended={platform === os}
                detectedArch={platform === 'macos' ? arch : null}
                formats={platformConfig.formats.map((f) => ({
                  type: f.type,
                  arch: f.arch,
                  filename: f.filename,
                  size: f.size,
                  url: f.url,
                }))}
                onDownload={handleDownload}
              />
            );
          })}
        </div>
      )}

      {/* System Requirements */}
      <div className="bg-secondary-50 dark:bg-secondary-800/50 rounded-xl p-6 sm:p-8 mb-8">
        <h2 className="text-xl font-bold text-secondary-900 dark:text-secondary-100 mb-6">
          {t('download.requirements.title')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
              <Monitor className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="font-medium text-secondary-900 dark:text-secondary-100">
                {t('download.requirements.os')}
              </p>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                {t('download.requirements.osValue')}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
              <svg
                className="h-4 w-4 text-purple-600 dark:text-purple-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-secondary-900 dark:text-secondary-100">
                {t('download.requirements.ram')}
              </p>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                {t('download.requirements.ramValue')}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center flex-shrink-0">
              <svg
                className="h-4 w-4 text-green-600 dark:text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-secondary-900 dark:text-secondary-100">
                {t('download.requirements.storage')}
              </p>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                {t('download.requirements.storageValue')}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center flex-shrink-0">
              <svg
                className="h-4 w-4 text-orange-600 dark:text-orange-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-secondary-900 dark:text-secondary-100">
                {t('download.requirements.gpu')}
              </p>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                {t('download.requirements.gpuValue')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 sm:p-8 mb-8">
        <h2 className="text-xl font-bold text-secondary-900 dark:text-secondary-100 mb-6">
          {t('download.features.title')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {['videoGeneration', 'localAI', 'cloudAI', 'exportFormats', 'autoUpdates'].map(
            (feature) => (
              <div key={feature} className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                <span className="text-secondary-700 dark:text-secondary-300">
                  {t(`download.features.${feature}`)}
                </span>
              </div>
            )
          )}
        </div>
      </div>

      {/* Version Info & Links */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-secondary-600 dark:text-secondary-400 border-t border-secondary-200 dark:border-secondary-700 pt-8">
        <div className="flex items-center gap-4">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : config ? (
            <>
              <span>
                {t('download.version')}: <strong>{config.currentVersion}</strong>
              </span>
              <span className="hidden sm:inline">|</span>
              <span>
                {t('download.releaseDate')}:{' '}
                <strong>{formatReleaseDate(config.releaseDate)}</strong>
              </span>
            </>
          ) : null}
        </div>
        <div className="flex items-center gap-4">
          <a
            href={config?.links.changelog ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            {t('download.links.changelog')}
            <ExternalLink className="h-3 w-3" />
          </a>
          <a
            href={config?.links.issues ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            {t('download.links.reportIssue')}
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
